import { toast } from "react-toastify";

import { AccessControlProvider } from "@refinedev/core";

import { resources } from "@/config/resources";
import { IUser } from "@/types";

import i18next from "i18next";

/**
 * Format error message for permission denial using translations
 */
const formatPermissionError = (action?: string, resource?: string): string => {
  const resourceName = resource ? resource.charAt(0).toUpperCase() + resource.slice(1) : "Resource";
  const actionName = action ? action.charAt(0).toUpperCase() + action.slice(1) : "Access";

  return i18next.t("errors.permissionDenied.actionFormat", {
    action: actionName,
    resource: resourceName,
  });
};

/**
 * Access control provider that checks user permissions for resources
 * Shows toast notifications when access is denied
 *
 * "User is not allowed to perform [action]:[ResourceName]"
 */
export const createAccessControlProvider = (getUserIdentity: () => Promise<IUser | null>): AccessControlProvider => {
  return {
    can: async ({ resource, action, params }) => {
      try {
        // Get current user
        const user = await getUserIdentity();

        if (!user) {
          return { can: false };
        }

        // Find resource configuration
        const resourceConfig = resources.find(r => r.name === resource);

        // If resource doesn't exist or has no role requirements, allow access
        if (!resourceConfig || !resourceConfig.meta?.requiredRoles) {
          return { can: true };
        }

        const requiredRoles = resourceConfig.meta.requiredRoles as string[];

        // Check if user has any of the required roles
        const hasPermission = requiredRoles.some(role => user.roles?.includes(role));

        if (!hasPermission) {
          const errorMessage = formatPermissionError(action, resource);

          // Only show toast for explicit access attempts (not menu visibility checks)
          // CanAccess component passes showToast: true in params
          if (params?.showToast) {
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "dark",
            });
          }

          return {
            can: false,
            reason: errorMessage,
          };
        }

        return { can: true };
      } catch (error) {
        // Handle getUserIdentity errors gracefully
        console.error("Access control check failed:", error);
        return { can: false };
      }
    },
  };
};
